/* Mod-Profil einer Karte: Liste der FGD-Dateien im Schlüssel xf_fgd der Welt-Entity (Dateinamen, durch ; getrennt, wie bei wad).
 * Alte Versionen des Editors ignorieren den Schlüssel; der Compiler auch. */
import EntDefs from './entdefs.ts';
import EntSpec from './entspec.ts';
import { libraryFromTexts, type FgdLibrary } from './fgdadapt.ts';
import { parseFgd } from './fgd.ts';

export const FGD_KEY = 'xf_fgd';
/** Ordner der FGD-Dateien unter den Assets des Servers */
export const FGD_DIR = 'fgd/';

const baseName = (p: string): string => p.replace(/\\/g, '/').replace(/^.*\//, '');

/** Dateinamen aus dem Schlüssel (Reihenfolge bleibt, ohne Ordner, ohne Doppelte, nur .fgd) */
export function fgdNames(kv: unknown): string[] {
  const seen = new Set<string>(), out: string[] = [];
  for (const part of String(kv ?? '').split(';')) {
    const n = baseName(part.trim());
    if (!/\.fgd$/i.test(n) || seen.has(n.toLowerCase())) continue;
    seen.add(n.toLowerCase()); out.push(n);
  }
  return out;
}

/** Name zur Liste hinzufügen (ohne Doppelte); liefert den neuen Schlüsselwert */
export function addFgdName(kv: unknown, name: string): string {
  return fgdNames(kv).concat(fgdNames(name)).filter((n, i, a) => a.findIndex((m) => m.toLowerCase() === n.toLowerCase()) === i).join(';');
}

/** Name zum Mod-Profil hinzufügen. Hat die Karte noch kein eigenes (kv leer), beginnt es mit der Vorgabe (auto), damit die Compiler-FGD erhalten bleibt;
 *  eine Spiel-FGD kommt dabei vor die Compiler-FGDs, weil spätere Dateien bei gleichem Feld gewinnen. */
export function addToProfile(kv: unknown, auto: string[], name: string): string {
  const cur = fgdNames(kv);
  if (cur.length) return addFgdName(kv, name);
  const isTool = (n: string) => COMPILER_FGDS.includes(n.toLowerCase());
  const add = fgdNames(name).filter((n) => !auto.some((a) => a.toLowerCase() === n.toLowerCase()));
  const base = auto.filter((n) => !isTool(n)), tools = auto.filter(isTool);
  return fgdNames(base.concat(add.filter((n) => !isTool(n)), tools, add.filter(isTool)).join(';')).join(';');
}

/** Name aus der Liste entfernen */
export function removeFgdName(kv: unknown, name: string): string {
  return fgdNames(kv).filter((n) => n.toLowerCase() !== name.toLowerCase()).join(';');
}

/** Namen in der Reihenfolge der Liste verschieben (dir -1 früher, +1 später); spätere Dateien überschreiben frühere */
export function moveFgdName(kv: unknown, name: string, dir: -1 | 1): string {
  const l = fgdNames(kv), i = l.findIndex((n) => n.toLowerCase() === name.toLowerCase()), j = i + dir;
  if (i < 0 || j < 0 || j >= l.length) return l.join(';');
  [l[i], l[j]] = [l[j], l[i]];
  return l.join(';');
}

/** Reihenfolge der Dateien samt @include: eine eingebundene Datei steht vor der, die sie einbindet (wie beim Lesen in Hammer), jede Datei nur einmal.
 *  texts: bereits gelesene Texte (Name klein). missing: Includes, deren Text noch fehlt (Name wie im @include, ohne Ordner). */
export function orderWithIncludes(names: string[], texts: Map<string, string>): { order: string[]; included: string[]; missing: string[] } {
  const order: string[] = [], seen = new Set<string>(), included: string[] = [], missing: string[] = [], listed = new Set(names.map((n) => n.toLowerCase()));
  const visit = (n: string, viaInclude: boolean, stack: string[]): void => {
    const k = n.toLowerCase();
    if (seen.has(k) || stack.includes(k)) return;
    const text = texts.get(k);
    if (text !== undefined) {
      for (const inc of parseFgd(text).includes.map(baseName)) {
        if (!/\.fgd$/i.test(inc)) continue;
        if (texts.has(inc.toLowerCase())) visit(inc, true, stack.concat(k));
        else if (!missing.some((m) => m.toLowerCase() === inc.toLowerCase())) missing.push(inc);
      }
    }
    if (seen.has(k)) return;
    seen.add(k); order.push(n);
    if (viaInclude && !listed.has(k)) included.push(n);
  };
  for (const n of names) visit(n, false, []);
  return { order, included, missing };
}

export interface FgdStatus {
  /** geladene Dateien in der Reihenfolge der Liste */
  loaded: string[];
  /** in der Liste, aber (noch) nicht geladen */
  missing: string[];
  /** nicht in der Liste, aber von einer Datei per @include eingebunden und geladen */
  included?: string[];
  /** per @include verlangt, aber nicht geladen */
  includeMissing?: string[];
  classes: number;
  warnings: string[];
}

/** FGDs einsetzen: gleichnamige Klassen der eingebauten Liste werden ersetzt. Ohne geladene Dateien gilt wieder nur die eingebaute Liste. */
export function applyFgds(names: string[], texts: Map<string, string>): FgdStatus {
  const have = (n: string): string | undefined => texts.get(n.toLowerCase());
  const missing = names.filter((n) => have(n) === undefined);
  const ord = orderWithIncludes(names.filter((n) => have(n) !== undefined), texts), loaded = ord.order;
  if (!loaded.length) { EntDefs.use(null); EntSpec.use(null); return { loaded, missing, classes: 0, warnings: [] }; }
  const lib: FgdLibrary = libraryFromTexts(loaded.map((n) => have(n) as string), EntDefs.groupOf, EntDefs.colors, loaded);
  EntDefs.use(lib.defs);
  EntSpec.use(lib.specs);
  return { loaded, missing, classes: lib.classes, warnings: lib.warnings, included: ord.included, includeMissing: ord.missing };
}

/** FGDs der Compiler-Werkzeuge: gehören zu jeder Karte (Entities und Schlüssel von SDHLT wie func_detail, info_portal, zhlt_*), solange kein eigenes Mod-Profil gespeichert ist */
export const COMPILER_FGDS = ['sdhlt.fgd'];
/** Vorgabe, wenn die Karte kein Mod-Profil gespeichert hat: zuerst die Spiel-FGD nach dem Kartennamen (op4_ → Opposing Force, de_ und cs_ → Counter-Strike), dann die der Compiler-Werkzeuge */
const BY_PREFIX: [RegExp, string][] = [[/^op4_/, 'halflife-op4.fgd'], [/^(de|cs)_/, 'halflife-cs.fgd']];
export function defaultFgds(fileName: string): string[] {
  const n = baseName(String(fileName || '')).toLowerCase();
  const hit = BY_PREFIX.find(([re]) => re.test(n));
  return (hit ? [hit[1]] : []).concat(COMPILER_FGDS);
}

/** Welche FGDs gelten: die gespeicherten; ohne Eintrag die Vorgabe nach Kartenname (nur wenn available sie kennt). auto = Vorgabe ist im Einsatz */
export function effectiveFgds(kv: unknown, fileName: string, available: (name: string) => boolean): { names: string[]; auto: boolean } {
  const saved = fgdNames(kv);
  if (saved.length) return { names: saved, auto: false };
  const d = defaultFgds(fileName).filter(available);
  return { names: d, auto: d.length > 0 };
}
